'use client';

import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
          size?: 'normal' | 'compact' | 'flexible' | 'invisible';
        }
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

interface TurnstileWidgetProps {
  onVerify: (token: string) => void;
  onExpire?: () => void;
  size?: 'normal' | 'compact' | 'invisible';
}

/**
 * Renders a Cloudflare Turnstile widget.
 * Requires NEXT_PUBLIC_TURNSTILE_SITE_KEY to be set.
 * If the key is missing the widget is invisible and no token is produced;
 * the backend blocks the request in production (fail closed).
 */
export function TurnstileWidget({ onVerify, onExpire, size = 'normal' }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (!siteKey || !containerRef.current) return;

    let mounted = true;
    let attempts = 0;

    const tryRender = () => {
      if (!mounted) return;
      if (window.turnstile && containerRef.current) {
        widgetId.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: onVerify,
          'expired-callback': () => {
            onExpire?.();
          },
          theme: 'dark',
          size,
        });
      } else if (attempts < 40) {
        attempts++;
        setTimeout(tryRender, 250);
      }
    };

    tryRender();

    return () => {
      mounted = false;
      if (widgetId.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetId.current);
        } catch {
          // widget may already be gone
        }
        widgetId.current = null;
      }
    };
  }, [siteKey, size]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!siteKey) return null;

  return <div ref={containerRef} />;
}
